import { supabase, getActiveSchema } from '../../../../supabaseClient';
import { PerformanceLogSchema, CompletedSessionSchema } from '@/types/zod.schemas';

/**
 * PERFORMANCE REPOSITORY
 * Specialized IO for workout tracking and session hydration.
 */

const getClient = () => {
    const schema = getActiveSchema();
    return supabase.schema(schema);
};

const SESSION_COLUMNS = 'id, program_day_id, name, session_focus, created_at, updated_at';
const COMPLETED_SESSION_COLUMNS = 'id, user_id, program_day_id, session_id, start_time, end_time, notes, created_at, updated_at';
const LOG_COLUMNS = 'id, completed_session_id, block_item_id, exercise_library_id, exercise_name_snapshot, target_snapshot, weight, reps, rpe, duration_seconds, distance_meters, set_number, created_at, updated_at';

export const PerformanceRepo = {
    async fetchSessionTemplate(dayId: string) {
        return await getClient()
            .from('sessions')
            .select(`
                ${SESSION_COLUMNS},
                blocks ( 
                    id, label, block_type, sort_order, 
                    block_items ( 
                        id, exercise_library_id, target_sets, target_reps, target_weight, target_rpe, tempo, set_targets, metric_type, sort_order, 
                        exercise_library ( name, technique_cues ) 
                    ) 
                )
            `)
            .eq('program_day_id', dayId)
            .maybeSingle();
    },

    async fetchGlobalHistory(userId: string) {
        return await getClient()
            .from('completed_sessions')
            .select('id, user_id, program_day_id, start_time, end_time, created_at, updated_at, program_days(label)')
            .eq('user_id', userId)
            .order('end_time', { ascending: false });
    },

    async fetchSessionDetails(sessionId: string, fkConstraint: string) {
        const { data, error } = await getClient()
            .from('performance_logs')
            .select(`
                ${LOG_COLUMNS},
                block_items!${fkConstraint} ( target_weight, target_reps, target_rpe, tempo, sort_order, exercise_library ( name ) ),
                exercise_library ( name )
            `)
            .eq('completed_session_id', sessionId)
            .order('created_at', { ascending: true });

        if (data) {
            data.forEach(log => {
                const result = PerformanceLogSchema.safeParse(log);
                if (!result.success) {
                    console.warn("[PerformanceRepo] Invalid log entry found:", result.error.format(), log);
                }
            });
        }
        return { data, error };
    },

    async insertCompletedSession(payload: any) {
        const { data, error } = await getClient()
            .from('completed_sessions')
            .insert([payload])
            .select(COMPLETED_SESSION_COLUMNS)
            .maybeSingle();

        if (data) CompletedSessionSchema.parse(data);
        return { data, error };
    },

    async updateCompletedSession(sessionId: string, payload: any) {
        const { data, error } = await getClient()
            .from('completed_sessions')
            .update(payload)
            .eq('id', sessionId)
            .select(COMPLETED_SESSION_COLUMNS)
            .maybeSingle();

        if (data) CompletedSessionSchema.parse(data);
        return { data, error };
    },

    async updatePerformanceLog(logId: string, payload: any) {
        const { data, error } = await getClient()
            .from('performance_logs')
            .update(payload)
            .eq('id', logId)
            .select(LOG_COLUMNS)
            .maybeSingle();

        if (data) PerformanceLogSchema.parse(data);
        return { data, error };
    },

    async insertPerformanceLogs(logs: any[]) {
        return await getClient()
            .from('performance_logs')
            .insert(logs);
    },

    async clearPerformanceLogs(completedSessionId: string) {
        return await getClient()
            .from('performance_logs')
            .delete()
            .eq('completed_session_id', completedSessionId);
    },

    async fetchCompletedSessionHeader(sessionId: string) {
        return await getClient()
            .from('completed_sessions')
            .select('id, user_id, program_day_id, start_time, end_time, created_at, updated_at, program_days(label)')
            .eq('id', sessionId)
            .maybeSingle();
    },

    async deleteSessionRecord(sessionId: string) {
        return await getClient()
            .from('completed_sessions')
            .delete()
            .eq('id', sessionId);
    },

    async fetchExerciseLibrary() {
        return await getClient()
            .from('exercise_library')
            .select('id, name')
            .order('name', { ascending: true });
    }
};
